import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as UserActions from './user.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { UserManagementService } from '../services/user-management.service';
import { MessageService } from 'primeng/api';

@Injectable()
export class UserEffects {
    private actions$ = inject(Actions);
    private userManagementService = inject(UserManagementService);
    private messageService = inject(MessageService);

    findUsers$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.findUsers),
        mergeMap(payload => this.userManagementService
            .findUsers(payload.page, payload.size, payload.keywords).pipe(
                map(users => UserActions.findUsersSuccess({ users })),
                catchError(error => of(UserActions.findUsersError({ error })))
            )
        )
    ));

    createUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.createUser),
        mergeMap(payload => this.userManagementService
            .createUser(payload.user).pipe(
                map(user => UserActions.createUserSuccess({ user })),
                catchError(error => of(UserActions.createUserError({ error })))
            )
        )
    ));

    updateUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.updateUser),
        mergeMap(payload => this.userManagementService
            .updateUser(payload.id, payload.user).pipe(
                map(user => UserActions.updateUserSuccess({ user })),
                catchError(error => of(UserActions.updateUserError({ error })))
            )
        )
    ));

    deleteUser$ = createEffect(() => this.actions$.pipe(
        ofType(UserActions.deleteUser),
        mergeMap(payload => this.userManagementService
            .deleteUser(payload.id).pipe(
                map(() => UserActions.deleteUserSuccess({ id: payload.id })),
                catchError(error => of(UserActions.deleteUserError({ error })))
            )
        )
    ));

    showSuccessMessage$ = createEffect(() => this.actions$.pipe(
        ofType(
            UserActions.createUserSuccess,
            UserActions.updateUserSuccess,
            UserActions.deleteUserSuccess
        ),
        tap((action) => {
            let message = 'Operation successful';
            if (action.type === UserActions.createUserSuccess.type) message = 'User created successfully';
            if (action.type === UserActions.updateUserSuccess.type) message = 'User updated successfully';
            if (action.type === UserActions.deleteUserSuccess.type) message = 'User deleted successfully';

            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: message
            });
        })
    ), { dispatch: false });

    showErrorMessage$ = createEffect(() => this.actions$.pipe(
        ofType(
            UserActions.findUsersError,
            UserActions.createUserError,
            UserActions.updateUserError,
            UserActions.deleteUserError
        ),
        tap((action) => {
            const error = action.error;
            let detail = 'An error occurred';

            if (error?.error?.detail) {
                if (typeof error.error.detail === 'string') {
                    detail = error.error.detail;
                } else if (Array.isArray(error.error.detail)) {
                    detail = error.error.detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
                }
            } else if (error?.message) {
                detail = error.message;
            }

            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: detail
            });
        })
    ), { dispatch: false });
}
